import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

export type MinimizedItem = {
  id: string;
  type: 'case' | 'issue-report' | 'feature-request';
  label: string;
  icon: ReactNode;
  onRestore: () => void;
  badge?: number; // For unread count on cases
};

type MinimizedDockContextType = {
  items: MinimizedItem[];
  addItem: (item: MinimizedItem) => void;
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<MinimizedItem>) => void;
};

const MinimizedDockContext = createContext<MinimizedDockContextType | undefined>(undefined);

export const MinimizedDockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<MinimizedItem[]>([]);

  const addItem = useCallback((item: MinimizedItem) => {
    setItems(prev => {
      // Remove existing item with same id if it exists
      const filtered = prev.filter(i => i.id !== item.id);
      return [...filtered, item];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<MinimizedItem>) => {
    setItems(prev => prev.map(item => (item.id === id ? { ...item, ...updates } : item)));
  }, []);

  return (
    <MinimizedDockContext.Provider value={{ items, addItem, removeItem, updateItem }}>
      {children}
    </MinimizedDockContext.Provider>
  );
};

export const useMinimizedDock = () => {
  const context = useContext(MinimizedDockContext);
  if (!context) {
    throw new Error('useMinimizedDock must be used within MinimizedDockProvider');
  }
  return context;
};
